import { Fragment, FunctionComponent, useContext } from 'react';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';
import { STEPS, STATES } from '../../constants/checkoutConstants';
import { trackCompletedStep } from '../../helpers/checkoutTracking';
import pick from 'lodash.pick';
import { billingFields, getAddressErrors } from '../../helpers/checkoutForms';
import { useAppDispatch, useCheckoutSelector } from '../../reducers/root';
import CheckoutSaveAndContinueAction from '../../components/shared/actions/CheckoutSaveAndContinueAction';
import CheckoutContinueAction from '../../components/shared/actions/CheckoutContinueAction';
import CheckoutCTAButton from '../../components/shared/CheckoutCTAButton';
import { logCheckoutError } from '../../helpers/logCheckoutError';
import { getGuestUserId } from '../../helpers/checkoutGuestUserToken';
import { guestCheckoutWalletUpdater } from '../helpers/guestCheckoutWalletUpdater';
import {
    togglePaymentSaving,
    bankAccountVaultedError,
    bankAccountSaved,
    toggleACHFormSubmissionError,
    completeOnPlatformWireTransfer,
    completeCheck,
    completeBankAccounts,
    toggleWireFormSubmissionError,
    achFormUpdateBlurred,
} from '../../actions/checkoutPaymentActions';
import { getAvailableBankAccountPaymentMethods } from '../../helpers/checkoutPayment';
import { isAddingBankAccount } from '../../reducers/checkoutPaymentReducer';
import { FormattedMessage } from 'dibs-react-intl';
import CheckoutMutationWrapper from '../../mutations/CheckoutMutationWrapper';
import { tradeFirmUpdateEoriVat } from '../../mutations/TradeFirmUpdateEoriVatMutation';
import { CheckoutServerVarsContext } from '../../components/shared/CheckoutServerVarsContext';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { SetupIntentResult } from '@stripe/stripe-js';
import styles from '../bankAccount/styles/checkoutBankAccountsSubmit.scss';

import { TransactionSaveStripePaymentMethodMutation } from '../../mutations/__generated__/TransactionSaveStripePaymentMethodMutation.graphql';
import { CheckoutBankAccountsSubmitStripe_viewer$key } from './__generated__/CheckoutBankAccountsSubmitStripe_viewer.graphql';
import { CheckoutBankAccountsSubmitStripe_buyer$key } from './__generated__/CheckoutBankAccountsSubmitStripe_buyer.graphql';
import { CheckoutBankAccountsSubmitStripe_buyer$data } from './__generated__/CheckoutBankAccountsSubmitStripe_buyer.graphql';
import { CheckoutBankAccountsSubmitStripe_transaction$key } from './__generated__/CheckoutBankAccountsSubmitStripe_transaction.graphql';

type ArrayElement<ArrayType extends readonly unknown[]> = ArrayType[number];

export type AddressType = NonNullable<
    ArrayElement<
        NonNullable<
            NonNullable<CheckoutBankAccountsSubmitStripe_buyer$data['addressBook']>['addressList']
        >
    >
>;

type CompleteBankAccountsType = {
    paymentMethodApiType: string;
    paymentMethodToken: string;
    lastValidBankAccountToken: string;
};

type Props = {
    viewer: CheckoutBankAccountsSubmitStripe_viewer$key;
    buyer: CheckoutBankAccountsSubmitStripe_buyer$key;
    transaction: CheckoutBankAccountsSubmitStripe_transaction$key | null;
};

const CheckoutBankAccountsSubmitStripe: FunctionComponent<Props> = ({
    buyer: buyerRef,
    transaction: transactionRef,
    viewer: viewerRef,
}) => {
    const environment = useRelayEnvironment();

    const viewer = useFragment(
        graphql`
            fragment CheckoutBankAccountsSubmitStripe_viewer on Viewer {
                id
            }
        `,
        viewerRef
    );

    const buyer = useFragment(
        graphql`
            fragment CheckoutBankAccountsSubmitStripe_buyer on User {
                serviceId
                profile {
                    email
                }
                wallet {
                    paymentMethods {
                        paymentMethod {
                            paymentMethodType
                            __typename
                            ... on ACH {
                                token
                                paymentProcessor
                            }
                        }
                    }
                }
                addressBook {
                    addressList {
                        serviceId
                        firstName
                        lastName
                        country
                        line1
                        line2
                        city
                        state
                        zipCode
                    }
                }
                tradeFirm {
                    serviceId
                }
            }
        `,
        buyerRef
    );

    const transaction = useFragment(
        graphql`
            fragment CheckoutBankAccountsSubmitStripe_transaction on Transaction {
                ...checkoutPayment_transaction
                shipment {
                    shippingAddress {
                        country
                        zipCode
                    }
                }
            }
        `,
        transactionRef
    );
    const availablePaymentMethodsMap = getAvailableBankAccountPaymentMethods(transaction);
    let paymentMethodsAvailable: string;

    const stripe = useStripe();
    const elements = useElements();
    const dispatch = useAppDispatch();

    const achShowFailedSubmissionError = useCheckoutSelector(
        state => state.payment.achShowFailedSubmissionError
    );
    const wireShowFailedSubmissionError = useCheckoutSelector(
        state => state.payment.wireShowFailedSubmissionError
    );
    const shippingAddressId = useCheckoutSelector(state => state.transaction.shippingAddressId);
    const shippingCountry = useCheckoutSelector(state => state.shipping.defaultCountry);
    const isPaymentSaving = useCheckoutSelector(state => state.payment.isPaymentSaving);
    const bankAccountState = useCheckoutSelector(state => state.payment.bankAccountState);
    const isAdding = isAddingBankAccount({ bankAccountState });
    const isBillingSameAsShipping = useCheckoutSelector(
        state => state.payment.isBillingSameAsShipping
    );
    const newBillingForm = useCheckoutSelector(state => state.payment.newBillingForm);
    const bankAccountMethodSelected = useCheckoutSelector(
        state => state.payment.bankAccountMethodSelected
    );
    const bankAccountSectionSelected = useCheckoutSelector(
        state => state.payment.bankAccountSectionSelected
    );
    const lastValidBankAccountToken = useCheckoutSelector(
        state => state.payment.lastValidBankAccountToken
    );
    const paymentMethodToken = useCheckoutSelector(state => state.transaction.paymentMethodToken);
    const paymentMethodApiType = useCheckoutSelector(
        state => state.transaction.paymentMethodApiType
    );
    const eoriNumber = useCheckoutSelector(state => state.payment.newEoriNumber);
    const vatId = useCheckoutSelector(state => state.payment.newVatId);

    if (
        availablePaymentMethodsMap.showACH &&
        availablePaymentMethodsMap.showCheck &&
        availablePaymentMethodsMap.showWireTransfer
    ) {
        paymentMethodsAvailable = 'achAndWireAndCheck';
    } else if (
        availablePaymentMethodsMap.showCheck &&
        availablePaymentMethodsMap.showWireTransfer
    ) {
        paymentMethodsAvailable = 'wireAndCheck';
    } else if (availablePaymentMethodsMap.showACH && availablePaymentMethodsMap.showWireTransfer) {
        paymentMethodsAvailable = 'achAndWire';
    } else if (availablePaymentMethodsMap.showACH) {
        paymentMethodsAvailable = 'ach';
    } else {
        paymentMethodsAvailable = 'wire';
    }

    const isDisabled = isPaymentSaving && bankAccountState !== STATES.STATE_SAVED;
    const { isMobile } = useContext(CheckoutServerVarsContext);
    const isWireTransferPayment = bankAccountMethodSelected === 'wireTransfer';

    //on clicking continue
    const onComplete = (): void => {
        switch (bankAccountMethodSelected) {
            case 'check': {
                trackCompletedStep(STEPS.STEP_PAYMENT);
                dispatch(completeCheck());
                break;
            }
            case 'bankAccount': {
                if (!isDisabled) {
                    const selectedAch =
                        buyer?.wallet?.paymentMethods
                            ?.map(method => method?.paymentMethod)
                            .find(method => {
                                return (
                                    method?.paymentMethodType === 'ACH' &&
                                    method?.token === paymentMethodToken
                                );
                            }) || null;
                    trackCompletedStep(STEPS.STEP_PAYMENT);
                    dispatch(
                        completeBankAccounts({
                            paymentMethodApiType,
                            paymentMethodToken,
                            lastValidBankAccountToken,
                            paymentProcessor: selectedAch?.paymentProcessor,
                        })
                    );
                }
            }
        }
    };

    const getCurrentShippingAddress = (): AddressType | null => {
        const addressList = buyer?.addressBook?.addressList || [];
        const address = addressList.find(a => a?.serviceId === shippingAddressId) || null;
        return {
            serviceId: address?.serviceId || null,
            firstName: address?.firstName || null,
            lastName: address?.lastName || null,
            country: address?.country || null,
            line1: address?.line1 || null,
            line2: address?.line2 || null,
            city: address?.city || null,
            state: address?.state || null,
            zipCode: address?.zipCode || null,
        };
    };

    const onSubmit = async (): Promise<void | null> => {
        dispatch(togglePaymentSaving(true));
        dispatch(toggleACHFormSubmissionError(false));
        const tradeFirmId = buyer?.tradeFirm?.serviceId;
        // get relevant billing address and validate
        const billingAddress: $TSFixMe =
            (isBillingSameAsShipping && shippingCountry === 'US') ||
            (isWireTransferPayment && isBillingSameAsShipping)
                ? pick(getCurrentShippingAddress(), billingFields)
                : newBillingForm;

        const addressErrors = getAddressErrors(billingAddress, true);

        const addressIsValid =
            (bankAccountSectionSelected !== 'wireAndCheck' &&
                (paymentMethodsAvailable === 'achAndWireAndCheck' ||
                    paymentMethodsAvailable === 'ach' ||
                    paymentMethodsAvailable === 'achAndWire')) ||
            isWireTransferPayment
                ? addressErrors.isValid
                : true;

        if (!stripe || !elements) {
            // disable form submission until Stripe.js has loaded.
            return null;
        }

        //trigger stripe form validation
        const { error: submitError } = await elements.submit();
        if (submitError) {
            dispatch(togglePaymentSaving(false));
            dispatch(toggleACHFormSubmissionError(true));
            // eslint-disable-next-line no-console
            console.error(submitError);
            return null;
        }

        //billing address validation
        if (!addressIsValid) {
            if (isWireTransferPayment) {
                dispatch(toggleWireFormSubmissionError(true));
                return null;
            }
            dispatch(toggleACHFormSubmissionError(true));
            dispatch(
                achFormUpdateBlurred({
                    accountType: true,
                    ownershipType: true,
                    routingNumber: true,
                    accountNumber: true,
                    firstNameOnAccount: true,
                    lastNameOnAccount: true,
                    businessNameOnAccount: true,
                })
            );
            dispatch(togglePaymentSaving(false));
            return null;
        } else if (isDisabled) {
            return null;
        }

        //handle check/wire transfer scenarios
        if (
            ((paymentMethodsAvailable === 'achAndWireAndCheck' ||
                paymentMethodsAvailable === 'achAndWire') &&
                bankAccountSectionSelected === 'wireAndCheck') ||
            (paymentMethodsAvailable !== 'achAndWireAndCheck' &&
                paymentMethodsAvailable !== 'ach' &&
                paymentMethodsAvailable !== 'achAndWire')
        ) {
            if (bankAccountMethodSelected === 'wireTransfer') {
                //wireTransfer is set as the default when the state initializes
                if (tradeFirmId && (eoriNumber || vatId)) {
                    try {
                        dispatch(togglePaymentSaving(true));
                        await tradeFirmUpdateEoriVat(environment, {
                            serviceId: tradeFirmId,
                            eoriNumber,
                            vatId,
                        });
                        dispatch(togglePaymentSaving(false));
                    } catch {
                        dispatch(togglePaymentSaving(false));
                    }
                }
                dispatch(completeOnPlatformWireTransfer(billingAddress));
            } else if (bankAccountMethodSelected === 'check') {
                dispatch(completeCheck());
            }
            dispatch(togglePaymentSaving(false));
            return null;
        }

        if (billingAddress.country !== 'US') {
            dispatch(togglePaymentSaving(false));
            dispatch(bankAccountVaultedError('ACH_NON_US_COUNTRY'));
            return null;
        }

        //handle ACH vaulting with stripe
        try {
            if (tradeFirmId && (eoriNumber || vatId)) {
                await tradeFirmUpdateEoriVat(environment, {
                    serviceId: tradeFirmId,
                    eoriNumber,
                    vatId,
                });
            }

            //create stripe setup intent and get client secret
            const createSetupIntentMutationResponse: $TSFixMe =
                await CheckoutMutationWrapper.commit(
                    'TransactionCreateStripeSetupIntent',
                    environment,
                    {
                        userId: buyer?.serviceId,
                        currency: 'USD',
                    },
                    isMobile
                );
            const clientSecret =
                createSetupIntentMutationResponse?.createStripeSetupIntent?.setupIntent
                    ?.clientSecret || '';
            const shippingAddress = transaction?.shipment?.shippingAddress;

            //confirm the setup intent with stripe and get the payment method ID
            const stripeResponse: SetupIntentResult = await stripe.confirmSetup({
                elements,
                clientSecret,
                confirmParams: {
                    //we need to set this, but we won't redirect upon successful vaulting
                    return_url: 'https://www.1stdibs.com/',
                    payment_method_data: {
                        billing_details: {
                            email: buyer?.profile?.email || '',
                            address: {
                                country: shippingAddress?.country || '',
                                postal_code: shippingAddress?.zipCode || '',
                            },
                        },
                    },
                },
                redirect: 'if_required',
            });

            if (stripeResponse?.error) {
                //error submitting stripe form
                dispatch(toggleACHFormSubmissionError(true));
                dispatch(bankAccountVaultedError('VAULT'));
                // eslint-disable-next-line no-console
                console.error(stripeResponse?.error);
            } else {
                const isGuestCheckout = !!getGuestUserId();
                const stripePaymentMethodId = stripeResponse?.setupIntent?.payment_method;
                if (typeof stripePaymentMethodId !== 'string') {
                    throw new Error('Invalid stripePaymentMethodId');
                }
                if (!buyer?.serviceId) {
                    throw new Error('No user id');
                }
                //save the new stripe payment method with BE
                await CheckoutMutationWrapper.commit<TransactionSaveStripePaymentMethodMutation>(
                    'TransactionSaveStripePaymentMethod',
                    environment,
                    {
                        userId: buyer.serviceId,
                        currency: 'USD',
                        paymentMethodToken: stripePaymentMethodId,
                        mockWallet: isGuestCheckout,
                    },
                    isMobile,
                    isGuestCheckout
                        ? store =>
                              guestCheckoutWalletUpdater<TransactionSaveStripePaymentMethodMutation>(
                                  store,
                                  viewer.id || '',
                                  'saveStripePaymentMethod'
                              )
                        : undefined
                );
                //success
                dispatch(toggleACHFormSubmissionError(false));
                dispatch(
                    bankAccountSaved({ token: stripePaymentMethodId, paymentProcessor: 'STRIPE' })
                );
                trackCompletedStep(STEPS.STEP_PAYMENT);
                dispatch(togglePaymentSaving(false));
            }

            dispatch(togglePaymentSaving(false));
        } catch (error: $TSFixMe) {
            logCheckoutError(error, 'ach');
            if (!error?.source) {
                dispatch(bankAccountVaultedError('VAULT'));
            } else {
                //vaulting error. Gerterror code from requestInfo.responsePayload
                const errorResponsePayload =
                    error?.source?.errors?.[0]?.requestInfo?.responsePayload;
                const { serviceResponseCode, httpCode } = errorResponsePayload || {};
                dispatch(bankAccountVaultedError(serviceResponseCode || httpCode));
            }
            dispatch(togglePaymentSaving(false));
        }
        return null;
    };

    const onClick = isAdding || isWireTransferPayment ? () => onSubmit() : onComplete;
    const submitLabel = isAdding ? <CheckoutSaveAndContinueAction /> : <CheckoutContinueAction />;

    return (
        <Fragment>
            <CheckoutCTAButton
                onClick={onClick}
                label={submitLabel}
                isSaving={isPaymentSaving}
                marginTop
                disabled={isPaymentSaving}
                dataTn="bankAccounts-save-button"
            />
            {((achShowFailedSubmissionError && bankAccountSectionSelected !== 'wireAndCheck') ||
                (wireShowFailedSubmissionError && isWireTransferPayment)) && (
                <div className={styles.formErrorNotice}>
                    <FormattedMessage
                        id="checkout.CheckoutBankAccountFormStripe.achFormErrorNotice"
                        defaultMessage="Please correct errors to continue."
                    />
                </div>
            )}
        </Fragment>
    );
};

export default CheckoutBankAccountsSubmitStripe;
